"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { imagineStore } from "@/stores/imagine.store";

const STATUS_COLORS = {
  success: "text-emerald-400",
  redirect: "text-amber-400",
  client: "text-red-400",
  server: "text-red-500",
  pending: "text-slate-300",
  error: "text-rose-400",
} as const;

function getStatusTone(status?: number, hasError?: boolean): string {
  if (hasError) return STATUS_COLORS.error;
  if (!status) return STATUS_COLORS.pending;
  if (status >= 200 && status < 300) return STATUS_COLORS.success;
  if (status >= 300 && status < 400) return STATUS_COLORS.redirect;
  if (status >= 400 && status < 500) return STATUS_COLORS.client;
  if (status >= 500) return STATUS_COLORS.server;
  return STATUS_COLORS.pending;
}

function formatTimestamp(timestamp?: string): string {
  if (!timestamp) return "--";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return timestamp;
  return date.toLocaleTimeString();
}

function formatDuration(duration?: number): string {
  if (typeof duration !== "number" || Number.isNaN(duration)) return "--";
  if (duration >= 1000) {
    return `${(duration / 1000).toFixed(2)}s`;
  }
  return `${Math.max(0, Math.round(duration))}ms`;
}

export function NetworkTab() {
  const sandboxInstrumented = imagineStore.use.sandboxState.instrumented();
  const entries = imagineStore.use.networkLogs.logs();
  const resetNetworkLogs = imagineStore.use.networkLogs.reset();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const wasAtBottomRef = useRef(true);

  // Auto scroll to bottom when new entries arrive if user is already at the end
  useEffect(() => {
    if (!listRef.current || entries.length === 0) return;
    requestAnimationFrame(() => {
      if (listRef.current && wasAtBottomRef.current) {
        listRef.current.scrollTop = listRef.current.scrollHeight;
      }
    });
  }, [entries]);

  const handleScroll = () => {
    if (!listRef.current) return;
    const container = listRef.current;
    const isAtBottom =
      container.scrollTop + container.clientHeight >=
      container.scrollHeight - 10;
    wasAtBottomRef.current = isAtBottom;
  };

  useEffect(() => {
    if (!sandboxInstrumented) {
      resetNetworkLogs();
      setSelectedId(null);
    }
  }, [sandboxInstrumented]);

  const selectedEntry = useMemo(() => {
    if (!selectedId) return null;
    return entries.find((entry) => entry.id === selectedId) ?? null;
  }, [entries, selectedId]);

  const clearEntries = () => {
    resetNetworkLogs();
    setSelectedId(null);
  };

  return (
    <div className="h-full w-full grid grid-cols-1 md:grid-cols-[2fr_3fr] bg-neutral-900 text-neutral-200">
      <div className="relative border-r border-neutral-800 overflow-scroll">
        <div className="flex items-center justify-between h-10 px-3 border-b border-neutral-800">
          <span className="text-sm font-medium uppercase tracking-wide text-neutral-400">
            Requests
          </span>
          {entries.length > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearEntries}
              className="text-xs h-6 px-2 text-neutral-300 hover:text-white hover:bg-neutral-800"
            >
              Clear
            </Button>
          )}
        </div>
        <div
          ref={listRef}
          onScroll={handleScroll}
          className="h-[calc(100%-2.5rem)] overflow-y-auto text-sm"
        >
          {!entries.length && (
            <div className="p-2 text-neutral-500 text-sm">
              Network activity will appear here.
            </div>
          )}
          {entries.map(({ id, request }) => {
            const status = request.status;
            const hasError = Boolean(request.error);
            const tone = getStatusTone(status, hasError);

            return (
              <button
                key={id}
                type="button"
                onClick={() => setSelectedId(id)}
                className={cn(
                  "w-full text-left px-4 py-3 border-b border-neutral-800 transition-colors",
                  selectedId === id
                    ? "bg-neutral-800/80"
                    : "hover:bg-neutral-800/40",
                )}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold uppercase text-neutral-300">
                    {(request.method ?? "GET").toString()}
                  </span>
                  <span className={cn("text-xs font-semibold", tone)}>
                    {hasError
                      ? "ERROR"
                      : typeof status === "number"
                        ? status
                        : "--"}
                  </span>
                  <span className="text-xs text-neutral-500">
                    {formatDuration(request.duration)}
                  </span>
                  <span className="ml-auto text-xs text-neutral-500">
                    {formatTimestamp(request.timestamp)}
                  </span>
                </div>
                <div className="mt-1 truncate text-sm text-neutral-300">
                  {request.url ?? "Unknown URL"}
                </div>
              </button>
            );
          })}
        </div>
      </div>
      <div className="hidden md:flex flex-col overflow-scroll">
        <div className="flex items-center justify-between h-10 px-3 border-b border-neutral-800">
          <span className="text-sm font-medium uppercase tracking-wide text-neutral-400">
            Details
          </span>
        </div>
        <div className="flex-1 overflow-y-auto p-2 text-sm space-y-4">
          {!selectedEntry && (
            <div className="text-neutral-500">
              Select a request on the left to inspect its payload.
            </div>
          )}
          {selectedEntry && (
            <div className="space-y-4">
              <section>
                <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                  Overview
                </h3>
                <dl className="mt-2 grid grid-cols-2 gap-2 text-neutral-300">
                  <div>
                    <dt className="text-[10px] uppercase text-neutral-500">
                      Method
                    </dt>
                    <dd className="font-medium">
                      {selectedEntry.request.method ?? "GET"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-neutral-500">
                      Status
                    </dt>
                    <dd className="font-medium">
                      {selectedEntry.request.error
                        ? "Error"
                        : (selectedEntry.request.status ?? "--")}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-neutral-500">
                      Duration
                    </dt>
                    <dd className="font-medium">
                      {formatDuration(selectedEntry.request.duration)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[10px] uppercase text-neutral-500">
                      Timestamp
                    </dt>
                    <dd className="font-medium">
                      {formatTimestamp(selectedEntry.request.timestamp)}
                    </dd>
                  </div>
                </dl>
              </section>

              <section>
                <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                  URL
                </h3>
                <p className="mt-2 break-all text-neutral-200">
                  {selectedEntry.request.url ?? "Unknown URL"}
                </p>
              </section>

              {selectedEntry.request.requestBody && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                    Request Body
                  </h3>
                  <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-neutral-950/60 p-3 text-xs text-neutral-100">
                    {selectedEntry.request.requestBody}
                  </pre>
                </section>
              )}

              {selectedEntry.request.responseBody && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                    Response Body
                  </h3>
                  <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-neutral-950/60 p-3 text-xs text-neutral-100">
                    {selectedEntry.request.responseBody}
                  </pre>
                </section>
              )}

              {!!Object.keys(selectedEntry.request.headers ?? {}).length && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                    Headers
                  </h3>
                  <dl className="mt-2 space-y-1 text-neutral-200">
                    {Object.entries(selectedEntry.request.headers ?? {}).map(
                      ([key, value]) => (
                        <div key={key} className="flex flex-col">
                          <span className="text-[10px] uppercase text-neutral-500">
                            {key}
                          </span>
                          <span className="text-xs">{value}</span>
                        </div>
                      ),
                    )}
                  </dl>
                </section>
              )}

              {selectedEntry.request.error && (
                <section>
                  <h3 className="text-xs uppercase tracking-wide text-neutral-400">
                    Error
                  </h3>
                  <pre className="mt-2 whitespace-pre-wrap break-words rounded bg-neutral-950/60 p-3 text-xs text-rose-300">
                    {selectedEntry.request.error.message}
                    {selectedEntry.request.error.stack
                      ? `\n${selectedEntry.request.error.stack}`
                      : ""}
                  </pre>
                </section>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
